import React, { useState, useEffect, useCallback } from 'react';
import { collection, doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import { useAcademicYear } from './useAcademicYear';
import './AdminReportsView.css';
import './AdminMoreDetailPages.css';
import StatCards from './StatCards';
import { scopeForAdmin, adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

const TERMS = [
  'Term 1',
  'Term 2',
  'Term 3'
];

function AdminReportsView({ adminData }) {
  const [reportCards, setReportCards] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateStatus, setGenerateStatus] = useState(null);
  const [genClass, setGenClass] = useState('');
  const [genTerm, setGenTerm] = useState('Term 1');
  const [selectedCard, setSelectedCard] = useState(null);
  const [editingComments, setEditingComments] = useState(false);
  const [editForm, setEditForm] = useState({ teacherComments: '', strengths: '', areasForImprovement: '' });
  const [saving, setSaving] = useState(false);

  const { selectedYear } = useAcademicYear();

  // School-scoped cache key builder — shared with AdminAnalyticsView so tabs
  // reuse each other's fetched collections.
  const ck = (p) => `${adminSchoolIds(adminData).join('|') || 'all'}:${p}`;

  const loadClasses = useCallback(async () => {
    try {
      let classesData = await dataCacheService.getDocs(ck('classes'), collection(db, 'classes'));
      classesData = scopeForAdmin(classesData, adminData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
    } catch (error) {
      console.error('Error loading classes:', error);
    }
  }, [adminData]);

  const loadStudents = useCallback(async () => {
    try {
      const studentsData = [];
      const perClass = await Promise.all(classes.map(cls =>
        dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students'))
      ));
      classes.forEach((cls, i) => perClass[i].forEach(d => {
        studentsData.push({ ...d, classId: cls.id, className: cls.name });
      }));
      setStudents(studentsData);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  }, [classes]);

  const loadReportCards = useCallback(async () => {
    try {
      setLoading(true);
      const cardsData = [];
      const perClass = await Promise.all(classes.map(async (cls) => {
        const studentsDocs = await dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students'));
        return Promise.all(studentsDocs.map(s =>
          dataCacheService.getDocs(ck(`classes/${cls.id}/students/${s.id}/reportCards`), collection(db, 'classes', cls.id, 'students', s.id, 'reportCards'))
            .then(cards => cards.map(d => ({ ...d, classId: cls.id, studentId: s.id })))
        ));
      }));
      perClass.flat(2).forEach(d => cardsData.push(d));
      setReportCards(cardsData);
    } catch (error) {
      console.error('Error loading report cards:', error);
    } finally {
      setLoading(false);
    }
  }, [classes]);

  useEffect(() => {
    loadClasses();
  }, [loadClasses]);

  useEffect(() => {
    if (classes.length > 0) {
      loadStudents();
      loadReportCards();
    }
  }, [classes, loadStudents, loadReportCards]);

  const handleGenerate = async () => {
    if (!genClass) {
      alert('Please select a class');
      return;
    }
    if (!selectedYear) {
      alert('No active academic year found. Please set up an academic year first.');
      return;
    }
    setGenerating(true);
    setGenerateStatus(null);

    try {
      const classStudents = students.filter(s => s.classId === genClass);
      if (classStudents.length === 0) {
        setGenerateStatus({ type: 'error', msg: 'No students found in this class' });
        setGenerating(false);
        return;
      }

      // Load subjects, assignments, attendance for the class (parallel, cached)
      const [classSubjects, classAssignments, classAttendance] = await Promise.all([
        dataCacheService.getDocs(ck(`classes/${genClass}/subjects`), collection(db, 'classes', genClass, 'subjects')),
        dataCacheService.getDocs(ck(`classes/${genClass}/assignments`), collection(db, 'classes', genClass, 'assignments')),
        dataCacheService.getDocs(ck(`classes/${genClass}/attendanceRecords`), collection(db, 'classes', genClass, 'attendanceRecords')),
      ]);

      // Load all grades for the class (nested under assignments)
      const allGrades = [];
      const gradesPerAssignment = await Promise.all(classAssignments.map(a =>
        dataCacheService.getDocs(ck(`classes/${genClass}/assignments/${a.id}/grades`), collection(db, 'classes', genClass, 'assignments', a.id, 'grades'))
      ));
      classAssignments.forEach((a, i) => gradesPerAssignment[i].forEach(d => {
        allGrades.push({ ...d, assignmentId: a.id });
      }));

      let generated = 0;
      let skipped = 0;

      for (const student of classStudents) {
        // Check if report card already exists for this term/year
        const existingCards = reportCards.filter(
          c => c.studentId === student.id && c.term === genTerm && c.academicYear === selectedYear.name
        );
        if (existingCards.length > 0) {
          skipped++;
          continue;
        }

        // Calculate subject-specific grades
        const subjectGrades = [];
        for (const subject of classSubjects) {
          // Get assignments for this subject
          const subjectAssignments = classAssignments.filter(a => a.subjectId === subject.id);
          
          // Get student's grades for this subject's assignments
          const studentSubjectGrades = allGrades.filter(g => 
            g.studentId === student.id && 
            subjectAssignments.some(a => a.id === g.assignmentId)
          );

          const subjectAverage = studentSubjectGrades.length > 0
            ? studentSubjectGrades.reduce((sum, g) => sum + (g.percentage || g.score || 0), 0) / studentSubjectGrades.length
            : 0;

          subjectGrades.push({
            id: crypto.randomUUID(),
            subjectId: subject.id,
            subjectName: subject.name,
            subjectCategory: subject.category || 'Other',
            average: Math.round(subjectAverage * 100) / 100,
            assignmentCount: subjectAssignments.length,
            completedAssignments: studentSubjectGrades.length,
            teacherComment: ''
          });
        }

        // Calculate overall metrics
        const studentGrades = allGrades.filter(g => g.studentId === student.id);
        const studentAttendance = classAttendance.filter(a => a.studentId === student.id);
        const eventParticipation = studentAttendance.filter(a => a.eventId).length;

        const averageGrade = studentGrades.length > 0
          ? studentGrades.reduce((sum, g) => sum + (g.percentage || g.score || 0), 0) / studentGrades.length
          : 0;

        const presentCount = studentAttendance.filter(a => a.status === 'present').length;
        const attendanceRate = studentAttendance.length > 0
          ? (presentCount / studentAttendance.length) * 100
          : 0;

        const totalAssignments = classAssignments.length;
        const completedAssignments = studentGrades.length;

        const reportCardId = crypto.randomUUID();
        const reportCardData = {
          id: reportCardId,
          studentId: student.id,
          classId: genClass,
          term: genTerm,
          academicYear: selectedYear.name,
          averageGrade: Math.round(averageGrade * 100) / 100,
          attendanceRate: Math.round(attendanceRate * 100) / 100,
          totalAssignments,
          completedAssignments,
          eventParticipation,
          subjectGrades,
          teacherComments: '',
          strengths: '',
          areasForImprovement: '',
          createdBy: adminData?.uid || '',
          createdAt: new Date().toISOString(),
          schoolId: adminData?.schoolId || null
        };

        // Save to Firestore: classes/{classId}/students/{studentId}/reportCards/{reportCardId}
        await setDoc(
          doc(db, 'classes', genClass, 'students', student.id, 'reportCards', reportCardId),
          reportCardData
        );
        generated++;
      }

      setGenerateStatus({
        type: 'success',
        msg: `Generated ${generated} report card(s)${skipped > 0 ? `, skipped ${skipped} existing` : ''}`
      });

      // Reload report cards
      dataCacheService.invalidatePath(`classes/${genClass}/students`);
      await loadReportCards();
    } catch (error) {
      console.error('Error generating report cards:', error);
      setGenerateStatus({ type: 'error', msg: 'Failed to generate: ' + error.message });
    } finally {
      setGenerating(false);
    }
  };

  const getFilteredReportCards = () => {
    if (selectedClass === 'all') return reportCards;
    return reportCards.filter(card => card.classId === selectedClass);
  };

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    return student ? (student.fullName || student.name || 'Unknown') : 'Unknown';
  };

  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls ? cls.name : 'Unknown';
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
    return new Date(dateValue).toLocaleDateString();
  };

  const getLetterGrade = (avg) => {
    if (avg >= 90) return 'A';
    if (avg >= 80) return 'B';
    if (avg >= 70) return 'C';
    if (avg >= 60) return 'D';
    return 'F';
  };

  const getGradeColor = (avg) => {
    if (avg >= 90) return '#16a34a';
    if (avg >= 80) return '#16a34a';
    if (avg >= 70) return '#ea580c';
    if (avg >= 60) return '#ca8a04';
    return '#dc2626';
  };

  const handleEditComments = () => {
    setEditForm({
      teacherComments: selectedCard.teacherComments || '',
      strengths: selectedCard.strengths || '',
      areasForImprovement: selectedCard.areasForImprovement || ''
    });
    setEditingComments(true);
  };

  const handleSaveComments = async () => {
    setSaving(true);
    try {
      const updatedData = {
        teacherComments: editForm.teacherComments,
        strengths: editForm.strengths,
        areasForImprovement: editForm.areasForImprovement
      };
      
      await updateDoc(
        doc(db, 'classes', selectedCard.classId, 'students', selectedCard.studentId, 'reportCards', selectedCard.id),
        updatedData
      );
      dataCacheService.invalidatePath(`classes/${selectedCard.classId}/students/${selectedCard.studentId}/reportCards`);

      // Update local state
      setSelectedCard({
        ...selectedCard,
        ...updatedData
      });
      
      // Update in report cards list
      setReportCards(prev => prev.map(card => 
        card.id === selectedCard.id ? { ...card, ...updatedData } : card
      ));
      
      setEditingComments(false);
    } catch (error) {
      console.error('Error saving comments:', error);
      alert('Failed to save comments: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  const filteredReportCards = getFilteredReportCards();

  return (
    <div className="admin-reports-view">
      {!selectedCard && (<>
      <div className="view-header">
        <h2>Report Cards</h2>
        <button
          className="generate-btn"
          onClick={() => setShowGenerateModal(true)}
        >
          + Generate Report Cards
        </button>
      </div>
      <StatCards items={[['Report Cards',reportCards.length],['Classes',classes.length],['Students',students.length],['Subjects',new Set(reportCards.flatMap(c => (c.subjects || c.subjectGrades || []).map(s => s.subjectName || s.subject || s.name)).filter(Boolean)).size],['Assignments',reportCards.reduce((n, c) => n + (c.totalAssignments || 0), 0)]]} />

      <div className="filter-bar">
        <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}>
          <option value="all">All Classes</option>
          {classes.map(cls => (
            <option key={cls.id} value={cls.id}>{cls.name}</option>
          ))}
        </select>
      </div>

      <p className="count-text">Report Cards: {filteredReportCards.length}</p>

      {filteredReportCards.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h3>No Report Cards</h3>
          <p>No report cards generated yet. Click "Generate Report Cards" to create them.</p>
        </div>
      ) : (
        <div className="report-cards-list">
          {filteredReportCards.map(card => (
            <div
              key={card.id}
              className="report-card"
              onClick={() => setSelectedCard(card)}
            >
              <div className="report-icon">📄</div>
              <div className="report-info">
                <h4>{getStudentName(card.studentId)}</h4>
                <p>Class: {getClassName(card.classId)}</p>
                <p>Term: {card.term || 'N/A'}</p>
                <p>Generated: {formatDate(card.createdAt)}</p>
                {card.academicYear && <p>Year: {card.academicYear}</p>}
              </div>
              <div className="report-stats">
                <div className="report-grade-badge" style={{ color: getGradeColor(card.averageGrade || 0) }}>
                  {getLetterGrade(card.averageGrade || 0)}
                </div>
                <span className="report-avg">{(card.averageGrade || 0).toFixed(1)}%</span>
              </div>
              <div className="report-status">
                <span className="status-badge completed">Generated</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <div className="generate-modal-overlay" onClick={() => !generating && setShowGenerateModal(false)}>
          <div className="generate-modal" onClick={e => e.stopPropagation()}>
            <div className="generate-modal-header">
              <h3>Generate Report Cards</h3>
              <button
                className="generate-close-btn"
                onClick={() => !generating && setShowGenerateModal(false)}
                disabled={generating}
              >✕</button>
            </div>

            <div className="generate-modal-body">
              {generateStatus && (
                <div className={`generate-status ${generateStatus.type}`}>
                  {generateStatus.msg}
                </div>
              )}

              <div className="generate-form-group">
                <label>Class *</label>
                <select
                  value={genClass}
                  onChange={(e) => setGenClass(e.target.value)}
                  disabled={generating}
                >
                  <option value="">Select Class</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                  ))}
                </select>
              </div>

              <div className="generate-form-group">
                <label>Term *</label>
                <select
                  value={genTerm}
                  onChange={(e) => setGenTerm(e.target.value)}
                  disabled={generating}
                >
                  {TERMS.map(term => (
                    <option key={term} value={term}>{term}</option>
                  ))}
                </select>
              </div>

              <div className="generate-info">
                <p>Academic Year: {selectedYear?.name || 'Loading...'}</p>
                <p>This will generate report cards for all students in the selected class based on their grades, attendance, and assignment data. Existing report cards for the same term/year will be skipped.</p>
              </div>

              <button
                className="generate-submit-btn"
                onClick={handleGenerate}
                disabled={generating || !genClass}
              >
                {generating ? 'Generating...' : 'Generate Report Cards'}
              </button>
            </div>
          </div>
        </div>
      )}

      </>)}

      {/* Detail Page */}
      {selectedCard && (
        <div className="detail-page">
          <button className="detail-back-button" onClick={() => { setSelectedCard(null); setEditingComments(false); }}>
            ◄ Back to Report Cards
          </button>
          <div className="report-detail-modal detail-card" style={{ maxWidth: '100%', maxHeight: 'none' }}>
            <div className="report-detail-header">
              <h3>Report Card Details</h3>
            </div>

            <div className="report-detail-body">
              <div className="report-detail-student">
                <h4>{getStudentName(selectedCard.studentId)}</h4>
                <p>Class: {getClassName(selectedCard.classId)}</p>
                <p>Term: {selectedCard.term} | Year: {selectedCard.academicYear}</p>
              </div>

              <div className="report-detail-summary">
                <div className="report-detail-card">
                  <p className="detail-label">Average Grade</p>
                  <p className="detail-value" style={{ color: getGradeColor(selectedCard.averageGrade || 0) }}>
                    {(selectedCard.averageGrade || 0).toFixed(1)}%
                  </p>
                  <p className="detail-letter" style={{ color: getGradeColor(selectedCard.averageGrade || 0) }}>
                    {getLetterGrade(selectedCard.averageGrade || 0)}
                  </p>
                </div>
                <div className="report-detail-card">
                  <p className="detail-label">Attendance</p>
                  <p className="detail-value">{(selectedCard.attendanceRate || 0).toFixed(1)}%</p>
                </div>
                <div className="report-detail-card">
                  <p className="detail-label">Assignments</p>
                  <p className="detail-value">{selectedCard.completedAssignments || 0}/{selectedCard.totalAssignments || 0}</p>
                </div>
                <div className="report-detail-card">
                  <p className="detail-label">Events</p>
                  <p className="detail-value">{selectedCard.eventParticipation || 0}</p>
                </div>
              </div>

              {/* Subject Grades Table */}
              <div className="report-detail-section">
                <h4>Subject Grades</h4>
                {selectedCard.subjectGrades && selectedCard.subjectGrades.length > 0 ? (
                  <div className="table-wrap">
                  <table className="subject-grades-table">
                    <thead>
                      <tr>
                        <th>Subject</th>
                        <th>Category</th>
                        <th>Average</th>
                        <th>Grade</th>
                        <th>Assignments</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCard.subjectGrades.map((sg, idx) => (
                        <tr key={idx}>
                          <td>{sg.subjectName}</td>
                          <td>{sg.subjectCategory}</td>
                          <td style={{ color: getGradeColor(sg.average || 0), fontWeight: 600 }}>
                            {(sg.average || 0).toFixed(1)}%
                          </td>
                          <td style={{ color: getGradeColor(sg.average || 0), fontWeight: 700 }}>
                            {getLetterGrade(sg.average || 0)}
                          </td>
                          <td>{sg.completedAssignments || 0}/{sg.assignmentCount || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                ) : (
                  <p className="report-detail-text">No subject grades available</p>
                )}
              </div>

              <div className="report-detail-section">
                <h4>Completion Rate</h4>
                <div className="completion-bar-container">
                  <div
                    className="completion-bar-fill"
                    style={{
                      width: `${selectedCard.totalAssignments > 0 ? ((selectedCard.completedAssignments / selectedCard.totalAssignments) * 100) : 0}%`
                    }}
                  />
                </div>
                <p className="completion-text">
                  {selectedCard.totalAssignments > 0
                    ? ((selectedCard.completedAssignments / selectedCard.totalAssignments) * 100).toFixed(1)
                    : 0}% completed
                </p>
              </div>

              <div className="report-detail-section">
                <div className="report-detail-section-header">
                  <h4>Teacher Comments</h4>
                  {!editingComments && (
                    <button className="edit-comments-btn" onClick={handleEditComments}>
                      Edit
                    </button>
                  )}
                </div>
                {editingComments ? (
                  <div className="edit-comments-form">
                    <div className="edit-form-group">
                      <label>General Comments</label>
                      <textarea
                        value={editForm.teacherComments}
                        onChange={(e) => setEditForm({ ...editForm, teacherComments: e.target.value })}
                        placeholder="Enter general teacher comments..."
                        rows={3}
                      />
                    </div>
                    <div className="edit-form-group">
                      <label>Strengths</label>
                      <textarea
                        value={editForm.strengths}
                        onChange={(e) => setEditForm({ ...editForm, strengths: e.target.value })}
                        placeholder="Enter student strengths..."
                        rows={2}
                      />
                    </div>
                    <div className="edit-form-group">
                      <label>Areas for Improvement</label>
                      <textarea
                        value={editForm.areasForImprovement}
                        onChange={(e) => setEditForm({ ...editForm, areasForImprovement: e.target.value })}
                        placeholder="Enter areas for improvement..."
                        rows={2}
                      />
                    </div>
                    <div className="edit-form-actions">
                      <button
                        className="cancel-edit-btn"
                        onClick={() => setEditingComments(false)}
                        disabled={saving}
                      >
                        Cancel
                      </button>
                      <button
                        className="save-edit-btn"
                        onClick={handleSaveComments}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    {selectedCard.teacherComments ? (
                      <p className="report-detail-text">{selectedCard.teacherComments}</p>
                    ) : (
                      <p className="report-detail-text empty-text">No comments added</p>
                    )}
                    {selectedCard.strengths && (
                      <div className="report-detail-subsection">
                        <h5>Strengths</h5>
                        <p className="report-detail-text">{selectedCard.strengths}</p>
                      </div>
                    )}
                    {selectedCard.areasForImprovement && (
                      <div className="report-detail-subsection">
                        <h5>Areas for Improvement</h5>
                        <p className="report-detail-text">{selectedCard.areasForImprovement}</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="report-detail-section">
                <p className="report-meta">Generated: {formatDate(selectedCard.createdAt)}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminReportsView;
